import React, { FunctionComponent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosPromise } from 'axios';
import CommonGrid from 'shared/commonGrid';
import moment from 'moment';
import {
 SelectionChangedEvent, ColDef, ColGroupDef, ColumnApi, RowNode, GridApi
} from '@ag-grid-enterprise/all-modules';
import { ROWSELECTION } from 'shared/commonGrid/grid-enums';
import { CollectionFunction, CollectionFunctionListData } from 'data/collectionFunction/collectionFunction.types';
import { ServerResponse, UserSettings, GridSettings } from 'data/types';
import { SupportedFeaturesCollectionFunctions } from 'data/supportedFeatures/supportedFeatures.types';
import { FormTemplateMapByKey } from 'data/template/template.types';
import ModalDialog from 'shared/modal/dialog';
import ActionMenuCellRenderer from 'shared/commonGrid/action-menu-cell-renderer';
import useConfirmActionDialog from 'utils/hooks/useConfirmActionDialog';
import ExportReportDialogBody, { ExportOption } from 'routes/AccessFunction/containers/AccessFunctionList/components/DialogBody/ExportReportDialogBody';
import CollectionFunctionListMenu from '../CollectionFunctionListMenu';
import CollectionFunctionMissingTemplateDialogBody from '../CollectionFunctionMissingTemplateDialogBody/CollectionFunctionMissingTemplateDialogBody';

type Props = {
  selectedCollectionFunction?: CollectionFunction;
  data: CollectionFunctionListData[];
  hasPrivilegeToCopy: boolean;
  hasPrivilegesToCreate: boolean
  hasPrivilegeToDelete: boolean
  hasPrivilegeToView: boolean
  noResults: boolean,
  beforeLogout: boolean,
  lastUpdatedAt: Date | undefined,
  columnDefs: (ColGroupDef | ColDef)[],
  suppressColumnVirtualisation?: boolean,
  enabledCollectionFunctions: SupportedFeaturesCollectionFunctions;
  collectionFunctionTemplates: FormTemplateMapByKey;
  onRefresh: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onCreateNew: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onSelectionChanged: (event: SelectionChangedEvent) => void;
  onSaveGridSettings: (gridSettings: GridSettings) => void;
  onGetGridSettings: () => { type: string, payload: AxiosPromise<ServerResponse<UserSettings>> };
  onView: () => void;
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
  handleExport?: (rowData: CollectionFunction[], headers: string[], exportOption: ExportOption) => void,
}

const CollectionFunctionGrid: FunctionComponent<Props> = ({
  hasPrivilegesToCreate,
  hasPrivilegeToDelete,
  hasPrivilegeToView,
  data = [],
  noResults,
  beforeLogout,
  lastUpdatedAt,
  columnDefs,
  suppressColumnVirtualisation,
  selectedCollectionFunction,
  enabledCollectionFunctions,
  collectionFunctionTemplates,
  onRefresh,
  onCreateNew,
  onSelectionChanged,
  onSaveGridSettings,
  onGetGridSettings,
  onView,
  onEdit,
  onCopy,
  onDelete,
  handleExport,
}: Props) => {
  const [confirmActionDialog, setConfirmActionDialog] = useConfirmActionDialog();
  const { t } = useTranslation();
  const commonGridRef = useRef<CommonGrid>(null);
  const [exportReportDialog, setExportReportDialog] = useConfirmActionDialog();
  let exportOption = {
    supportedFileFormats: ['json'],
    fileFormat: 'json' as 'json' | 'csv' | 'xml' | 'pdf' | 'txt',
    filename: `cfs_export_${moment().valueOf()}`,
    isExporting: false
  };
  const components = {
    actionMenuCellRenderer: ActionMenuCellRenderer,
  };

  const showPopupForUnavailableTemplate = (title: string, collectionFunctionTag: string) => {
    const collectionFunctionTitle = enabledCollectionFunctions[collectionFunctionTag]?.title ?? '';

    setConfirmActionDialog(
      t('collectionFunction.grid.missingTemplate.title', { title }),
      <CollectionFunctionMissingTemplateDialogBody name={collectionFunctionTitle} />,
      t('common.button.close'),
    );
  };

  const isTemplateAvailable = ({ tag }: CollectionFunction) => {
    return collectionFunctionTemplates[tag] != null;
  };

  const handleEdit = () => {
    if (selectedCollectionFunction == null) {
      return;
    }

    if (isTemplateAvailable(selectedCollectionFunction)) {
      onEdit();
    } else {
      showPopupForUnavailableTemplate(t('collectionFunction.grid.edit'), selectedCollectionFunction.tag);
    }
  };

  const handleCopy = () => {
    if (selectedCollectionFunction == null) {
      return;
    }

    if (isTemplateAvailable(selectedCollectionFunction)) {
      onCopy();
    } else {
      showPopupForUnavailableTemplate(t('collectionFunction.grid.copy'), selectedCollectionFunction.tag);
    }
  };

  const handleView = () => {
    if (selectedCollectionFunction == null) {
      return;
    }

    if (isTemplateAvailable(selectedCollectionFunction)) {
      onView();
    } else {
      showPopupForUnavailableTemplate(t('collectionFunction.grid.view'), selectedCollectionFunction.tag);
    }
  };

  const onHandleExport = () => {
    setExportReportDialog(
      t('collectionFunction.exportPopup.title'),
      <ExportReportDialogBody
        exportOption={exportOption}
        handleExportOptionsChange={handleExportOptionsChange}
      >
        {t('collectionFunction.exportPopup.message')}
      </ExportReportDialogBody>,
      t('collectionFunction.exportPopup.action'),
      onSubmitCallback
    );
  };

  const onSubmitCallback = () => {
    const rowData: CollectionFunction[] = [];
    let headers: string[] = [];
    (commonGridRef?.current?.gridApi as GridApi).forEachNodeAfterFilterAndSort(
      (node: RowNode) => {
        rowData.push(node.data);
      }
    );

    (commonGridRef?.current?.gridColumnApi as ColumnApi)
      .getAllGridColumns()
      .forEach((column, index) => {
        headers.push(
          (commonGridRef?.current
            ?.gridColumnApi as ColumnApi).getDisplayNameForColumn(
            column,
            'header'
          )
        );
      });

    if (headers && headers.length > 0) {
      headers = headers.splice(2);
    }

    if (handleExport) {
      handleExport(rowData, headers, exportOption);
    }
  };

  const handleExportOptionsChange = (option: ExportOption) => {
    exportOption = option;
  };

  return (
    <div className="grid-container" style={{ height: 'inherit' }}>
      <CommonGrid
        ref={commonGridRef}
        createNewText={t('collectionFunction.grid.createNew')}
        pagination
        enableBrowserTooltips
        rowSelection={ROWSELECTION.SINGLE}
        hasPrivilegeToCreateNew={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        hasPrivilegetoView={hasPrivilegeToView}
        rowData={data}
        noResults={noResults}
        isBeforeLogout={beforeLogout}
        columnDefs={columnDefs}
        components={components}
        suppressColumnVirtualisation={suppressColumnVirtualisation}
        lastUpdatedAt={lastUpdatedAt}
        handleCreateNew={onCreateNew}
        onRefresh={onRefresh}
        onSelectionChanged={onSelectionChanged}
        saveGridSettings={onSaveGridSettings}
        getGridSettingsFromServer={onGetGridSettings}
        onActionMenuItemClick={handleView}
        onExport={onHandleExport}
      >
        <CollectionFunctionListMenu
          selectedCollectionFunction={selectedCollectionFunction}
          hasPrivilegeToCreateNew={hasPrivilegesToCreate}
          hasPrivilegeToDelete={hasPrivilegeToDelete}
          onEdit={handleEdit}
          onCopy={handleCopy}
          onDelete={onDelete}
        />
      </CommonGrid>
      <ModalDialog
        width="500px"
        isOpen={confirmActionDialog.isOpen}
        title={confirmActionDialog.title}
        customComponent={confirmActionDialog.customComponent}
        actionText={confirmActionDialog.actionText}
        onSubmit={confirmActionDialog.onSubmit}
        onClose={confirmActionDialog.onClose}
        doNotDisplayCancelButton={confirmActionDialog.hideCancel}
      />
      <ModalDialog
        width="500px"
        isOpen={exportReportDialog.isOpen}
        title={exportReportDialog.title}
        customComponent={exportReportDialog.customComponent}
        actionText={exportReportDialog.actionText}
        onSubmit={exportReportDialog.onSubmit}
        onClose={exportReportDialog.onClose}
        doNotDisplayCancelButton={exportReportDialog.hideCancel}
      />
    </div>
  );
};

export default CollectionFunctionGrid;
